import {FC, useState, useMemo, useEffect} from 'react'
import React from 'react'
import {KTSVG} from '../../../../_metronic/helpers'
import {useAuth} from '../../auth'
import {QHSE_PERMS} from '../core/permissions'
import {
  filterVesselsForManagementList,
  getUserCompanyGroupAdminId,
  isBroadFleetPickerUser,
  isLikelyVesselAssignedUser,
} from '../core/qhseUserContext'
import {toast} from 'react-toastify'

import {getCompanyList, getCompanyAdminList} from '../../Management/core/_requests'
import type {Vessel} from '../../Management/core/_models'
import {
  fetchAccessibleVesselsForCurrentUser,
  normalizeAccessibleVessels,
} from '../../../shared/access/accessibleVessels'
import {
  listAuditKinds,
  listAuditPlans,
  bulkCreateAuditPlans, // ✅ use audit-specific bulk create
  getAudit,
  getAuditPlan,
  listPorts,
  listCrewsLite,
  createAudit,
  linkAuditPlanToAudit,
} from '../core/_requests'

import type {QhseAuditKind, AuditPlanDto, VesselLite, AuditDto} from '../core/_models'

import AddAuditPlanModal from './AddAuditPlanModal'
import {AuditPlanActionsModal} from './AuditPlanActionsModal'
import AddAuditModal from './AddAuditModal'
import {ViewAuditModal} from './ViewAuditModal'
import {getRanksforList} from '../../Crewing/core/_requests'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import { TableSkeleton, PlanningCalendarSkeleton } from '../../../shared/components/LoadingSkeletons'

const toastApiError = (e: unknown, fallback: string) => {
  const anyE = e as {response?: {status?: number; data?: {message?: string}}}
  if (anyE?.response?.status === 403) {
    toast.error('You do not have access to perform this action.', {position: 'top-center'})
    return
  }
  toast.error(fallback, {position: 'top-center'})
}

// Add filter data JSON
const filterData = {
  fleets: [
    {value: '', label: 'All Fleets'},
    {value: 'fleet1', label: 'Fleet 1'},
    {value: 'fleet2', label: 'Fleet 2'},
    {value: 'fleetARS', label: 'Fleet ARS'},
  ],
  vessels: [
    {value: '', label: 'All Vessels'},
    {value: 'Agile', label: 'Agile'},
    {value: 'Dubai Office', label: 'Dubai Office'},
    {value: 'Eagle S', label: 'Eagle S'},
    {value: 'India Office', label: 'India Office'},
    {value: 'Lion S', label: 'Lion S'},
    {value: 'Marabella Sun (DNU)', label: 'Marabella Sun (DNU)'},
    {value: 'Nari Strength', label: 'Nari Strength'},
    {value: 'Sinar G', label: 'Sinar G'},
    {value: 'Tranquil Sea', label: 'Tranquil Sea'},
    {value: 'Troy', label: 'Troy'},
  ],
  years: (() => {
    const currentYear = new Date().getFullYear()
    const years = []
    for (let i = currentYear - 2; i <= currentYear + 2; i++) {
      years.push({value: i, label: i.toString(), isCurrent: i === currentYear})
    }
    return years
  })(),
}

// --- SVG Icon Helper Component ---
const SVGIcon: FC<{path: string; className?: string}> = ({path, className = ''}) => (
  <span className={`svg-icon ${className}`}>
    <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
      <path d={path} fill='currentColor' />
    </svg>
  </span>
)

// ---- Helpers & constants (define once near top) ----
const ALL_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const

const monthLabel = (iso: string) => {
  if (!iso) return ''
  const parts = String(iso).split('-')
  if (parts.length >= 2) {
    const m = parseInt(parts[1], 10) - 1
    if (m >= 0 && m < 12) return ALL_MONTHS[m]
  }
  return ALL_MONTHS[new Date(iso).getMonth()]
}

const yearOf = (iso: string) => {
  if (!iso) return 0
  const match = String(iso).match(/^(\d{4})/)
  return match ? parseInt(match[1], 10) : new Date(iso).getFullYear()
}

// --- TYPE DEFINITIONS ---
type AuditPlan = {
  id: number
  vesselName: string
  auditName: string
  month: string
  year: number
  status?: 'overdue' | 'planned_done' | 'next_30_days' | 'upcoming' // optional - for demo/testing
}

type NewPlanData = {
  auditName: string // kindId as string
  vesselNames: string[] // vesselIds as string[]
  months: typeof ALL_MONTHS[number][] // 'Jan' | 'Feb' | ... | 'Dec'
  year: number
}

// Add filter state type
type FilterState = {
  fleet: string
  vessel: string
  year: number
}

// Helper function to determine plan status
const getPlanStatus = (
  plan: AuditPlan
): 'overdue' | 'planned_done' | 'next_30_days' | 'upcoming' => {
  const currentDate = new Date()
  const monthIndex = ALL_MONTHS.indexOf(plan.month as typeof ALL_MONTHS[number])
  const planDate = new Date(plan.year, monthIndex, 1)
  const daysDiff = Math.ceil((planDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24))

  if (planDate < currentDate) return 'overdue'
  if (daysDiff <= 30 && daysDiff >= 0) return 'next_30_days'
  return 'upcoming'
}

// Helper function to get color classes based on status
const getStatusColorClasses = (status: string) => {
  switch (status) {
    case 'overdue':
      return {
        backgroundColor: '#ffebee',
        borderColor: '#f44336',
        textColor: '#c62828',
      }
    case 'planned_done':
      return {
        backgroundColor: '#e8f5e8',
        borderColor: '#4caf50',
        textColor: '#2e7d32',
      }
    case 'next_30_days':
      return {
        backgroundColor: '#fce4ec',
        borderColor: '#8e24aa',
        textColor: '#6a1b9a',
      }
    default: // upcoming
      return {
        backgroundColor: '#f3f4f6',
        borderColor: '#9ca3af',
        textColor: '#374151',
      }
  }
}

// --- CUSTOM MULTI-SELECT DROPDOWN COMPONENT ---
interface MultiSelectDropdownProps {
  label: string
  options: string[]
  selectedOptions: string[]
  onChange: (selected: string[]) => void
  error?: string
}

const MultiSelectDropdown: FC<MultiSelectDropdownProps> = ({
  label,
  options,
  selectedOptions,
  onChange,
  error,
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const handleToggle = () => setIsOpen(!isOpen)

  const handleCheckboxChange = (option: string) => {
    const newSelection = selectedOptions.includes(option)
      ? selectedOptions.filter((item) => item !== option)
      : [...selectedOptions, option]
    onChange(newSelection)
  }

  const handleSelectAll = () => {
    if (selectedOptions.length === options.length) {
      onChange([])
    } else {
      onChange(options)
    }
  }

  const displayLabel =
    selectedOptions.length > 0 ? `${selectedOptions.length} selected` : `Select ${label}`

  return (
    <div className='dropdown'>
      <button
        type='button'
        className={`form-control text-start d-flex justify-content-between align-items-center ${
          error ? 'is-invalid' : ''
        }`}
        onClick={handleToggle}
      >
        <span>{displayLabel}</span>
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
      </button>
      {isOpen && (
        <div className='dropdown-menu show w-100' style={{maxHeight: '200px', overflowY: 'auto'}}>
          <div className='px-3 py-2'>
            <div className='form-check'>
              <input
                className='form-check-input'
                type='checkbox'
                id={`select-all-${label}`}
                checked={selectedOptions.length === options.length}
                onChange={handleSelectAll}
              />
              <label className='form-check-label' htmlFor={`select-all-${label}`}>
                Select All
              </label>
            </div>
          </div>
          <div className='dropdown-divider'></div>
          {options.map((option) => (
            <div key={option} className='px-3 py-1'>
              <div className='form-check'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id={`${label}-${option}`}
                  checked={selectedOptions.includes(option)}
                  onChange={() => handleCheckboxChange(option)}
                />
                <label className='form-check-label' htmlFor={`${label}-${option}`}>
                  {option}
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
      {error && <div className='invalid-feedback d-block'>{error}</div>}
    </div>
  )
}

// --- ADD PLAN MODAL COMPONENT ---
interface AddPlanModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: (data: NewPlanData, saveAndAddNew: boolean) => void
  kinds: QhseAuditKind[] // NEW
  vessels: VesselLite[] // NEW
}

// --- LEGEND COMPONENT ---
const StatusLegend: FC = () => {
  return (
    <div className='d-flex align-items-center gap-4 mb-3'>
      <div className='d-flex align-items-center gap-2'>
        <div
          style={{
            width: '16px',
            height: '16px',
            backgroundColor: '#f44336',
            borderRadius: '4px',
          }}
        ></div>
        <span className='fs-7 text-muted'>Overdue Plan</span>
      </div>
      <div className='d-flex align-items-center gap-2'>
        <div
          style={{
            width: '16px',
            height: '16px',
            backgroundColor: '#4caf50',
            borderRadius: '4px',
          }}
        ></div>
        <span className='fs-7 text-muted'>Planned & Done</span>
      </div>
      <div className='d-flex align-items-center gap-2'>
        <div
          style={{
            width: '16px',
            height: '16px',
            backgroundColor: '#8e24aa',
            borderRadius: '4px',
          }}
        ></div>
        <span className='fs-7 text-muted'>Next 30 Days</span>
      </div>
    </div>
  )
}

// --- MAIN AUDIT PLANNING COMPONENT ---
const AuditPlanning: FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [sortConfig, setSortConfig] = useState({key: 'vesselName', direction: 'asc'})
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const [vesselMap, setVesselMap] = useState<Record<number, string>>({})
  // add below existing maps
  const [portMap, setPortMap] = useState<Record<number, string>>({})
  const [vesselTypeMap, setVesselTypeMap] = useState<Record<number, string>>({})

  // crews for internal-Auditor fallback (same shape as Index)
  const [crews, setCrews] = useState<
    Array<{id: number; fullName: string; rankId?: number; rankName?: string; vesselId?: number}>
  >([])

  const [kindMap, setKindMap] = useState<Record<number, string>>({})

  const [rankMap, setRankMap] = useState<Record<number, string>>({})

  // Add filter state
  const [filters, setFilters] = useState<FilterState>({
    fleet: '',
    vessel: '',
    year: new Date().getFullYear(),
  })

  const [companies, setCompanies] = useState<Array<{id: number; name: string}>>([])
  const [subcompanies, setSubcompanies] = useState<
    Array<{id: number; name: string; companyId: number}>
  >([])
  const [planFilters, setPlanFilters] = useState({companyId: '', subcompanyId: ''})

  const {currentUser, can, permissionsLoaded} = useAuth()
  const { state: globalState, vessels: globalVessels } = useGlobalFilter()
  const shipboardUser = isLikelyVesselAssignedUser(currentUser)
  const broadFleet = isBroadFleetPickerUser(currentUser)
  const myCgaId = getUserCompanyGroupAdminId(currentUser)
  const isTopLevel = broadFleet
  const effectiveCompanyId = isTopLevel ? undefined : myCgaId ?? undefined

  const canReadPlan = permissionsLoaded && can(QHSE_PERMS.AUDIT_PLAN_READ)
  const canCreatePlan = permissionsLoaded && can(QHSE_PERMS.AUDIT_PLAN_CREATE)
  const canUpdatePlan = permissionsLoaded && can(QHSE_PERMS.AUDIT_PLAN_UPDATE)
  const canReadAudit = permissionsLoaded && can(QHSE_PERMS.AUDIT_READ)
  const canCreateAudit = permissionsLoaded && can(QHSE_PERMS.AUDIT_CREATE)

  const [kinds, setKinds] = useState<QhseAuditKind[]>([])
  const [vessels, setVessels] = useState<VesselLite[]>([])
  const [plans, setPlans] = useState<AuditPlanDto[]>([])
  const [loading, setLoading] = useState(true)

  const [planActionsOpen, setPlanActionsOpen] = useState(false)
  const [viewOpen, setViewOpen] = useState(false)
  const [viewing, setViewing] = useState<AuditDto | null>(null)

  const [selectedPlan, setSelectedPlan] = useState<AuditPlanDto | null>(null)

  // to open AddAudit from a plan, prefilled:
  const [addFromPlan, setAddFromPlan] = useState<{
    vesselId: number
    auditKindId: number
    planId: number
    planMonthDate: string
  } | null>(null)

  const [portsList, setPortsList] = useState<Array<{id: number; name: string}>>([])

  const filteredPlans = useMemo(() => {
    return plans.filter((plan) => yearOf(plan.planDate) === filters.year)
  }, [plans, filters.year])

  const toViewPlan = useMemo(() => {
    return (p: AuditPlanDto): AuditPlan => {
      const gv = (globalVessels || []).find((v: any) => Number(v.id) === Number(p.vesselId))
      const vName =
        vesselMap[p.vesselId] ||
        (gv as any)?.fleet_name ||
        gv?.name ||
        (p.vesselName && p.vesselName.trim()) ||
        (p.vesselId ? `Vessel ${p.vesselId}` : '-')
      const aName =
        kindMap[p.auditKindId] ||
        (p.auditKindName && p.auditKindName.trim()) ||
        (p.auditKindId ? `Audit ${p.auditKindId}` : '-')
      return {
        id: p.id,
        vesselName: vName,
        auditName: aName,
        month: monthLabel(p.planDate),
        year: yearOf(p.planDate),
        status: (p as any).status,
      }
    }
  }, [vesselMap, kindMap, globalVessels])

  useEffect(() => {
    const load = async () => {
      try {
        const k = await listAuditKinds()
        setKinds(k)

        const vesselList: Vessel[] = normalizeAccessibleVessels(
          await fetchAccessibleVesselsForCurrentUser()
        )
        const vesselsForUser = filterVesselsForManagementList(vesselList, currentUser)

        setVessels(
          vesselsForUser.map((v) => ({
            id: Number(v.id),
            name: (v as any).fleet_name || v.name || `Vessel ${v.id}`,
            vesselType: (v as any).vesselType || (v as any).type || undefined,
          }))
        )

        // --- Build id -> name maps once
        const vMap: Record<number, string> = {}
        for (const v of vesselsForUser as any[]) {
          const id = Number(v.id)
          vMap[id] = (v as any).fleet_name || v.name || `-`
        }
        setVesselMap(vMap)

        // kinds list -> map
        const kindsList: QhseAuditKind[] = Array.isArray(k) ? k : []
        const kMapLocal: Record<number, string> = {}
        for (const kind of kindsList) {
          kMapLocal[Number(kind.id)] = String(kind.name || '')
        }
        setKindMap(kMapLocal)

        // 🔎 ADD: build id -> rankName map once (same as Index)
        try {
          const ranks = await getRanksforList().catch(() => [])
          const rMap: Record<number, string> = {}
          ;(Array.isArray(ranks) ? ranks : []).forEach((r: any) => {
            if (r?.id != null) rMap[Number(r.id)] = String(r.rank ?? r.name ?? '')
          })
          setRankMap(rMap)
        } catch {
          setRankMap({})
        }

        // ⬇️ Ports (normalize main_port -> name)
        try {
          const ports = await listPorts()
          const pMap: Record<number, string> = {}
          for (const p of ports as any[]) {
            const id = Number(p.id)
            pMap[id] = String(p.name || '')
          }
          setPortMap(pMap)
          setPortsList(
            (ports as any[]).map((p) => ({id: Number(p.id), name: String(p.name || '')}))
          )
        } catch {
          setPortMap({})
          setPortsList([])
        }

        // ⬇️ Vessel type map (id -> type) for consistent view patching
        const vtMap: Record<number, string> = {}
        for (const v of vesselsForUser as any[]) {
          const id = Number(v.id)
          const rawType = v.vesselType ?? v.type ?? v.vessel_type ?? ''
          vtMap[id] = rawType ? String(rawType) : ''
        }
        setVesselTypeMap(vtMap)

        // ⬇️ Crews list for internal Auditor fallback (same helper used in Index)
        try {
          const rawCrews = await listCrewsLite()
          const normalized = (Array.isArray(rawCrews) ? rawCrews : [])
            .map((c: any) => ({
              id: Number(c.id),
              fullName: String(c.fullName ?? c.name ?? ''),
              rankId: c.rankId != null ? Number(c.rankId) : undefined,
              rankName: c.rankName ?? undefined,
              vesselId: c.vesselId != null ? Number(c.vesselId) : undefined,

              // NEW: make active explicit; default to true if absent
              active:
                typeof c.active === 'boolean'
                  ? Boolean(c.active)
                  : typeof c.isActive === 'boolean'
                  ? Boolean(c.isActive)
                  : true,

              // (optional, helps future filters)
              companyGroupAdminId:
                c.companyGroupAdminId != null
                  ? Number(c.companyGroupAdminId)
                  : c.cgaid?.id != null
                  ? Number(c.cgaid.id)
                  : undefined,
              companyAdminId:
                c.companyAdminId != null
                  ? Number(c.companyAdminId)
                  : c.caId ?? c.companyId != null
                  ? Number(c.caId ?? c.companyId)
                  : undefined,
            }))
            .filter((x) => Number.isFinite(x.id))
          setCrews(normalized)
        } catch (e) {
          console.warn('Failed to load crews for internal auditor fallback', e)
          setCrews([])
        }

        const companiesLite = Array.from(
          new Map(
            vesselList
              .map((v) => {
                const id = Number((v as any).companyGroupAdmin?.id ?? (v as any).companyGroupId)
                const name =
                  (v as any).companyGroupAdmin?.companyGroupName ??
                  (v as any).companyGroupAdmin?.name ??
                  `Company ${id}`
                return id ? ([id, {id, name}] as const) : null
              })
              .filter(Boolean) as Array<[number, {id: number; name: string}]>
          ).values()
        )

        const subcompaniesLite = Array.from(
          new Map(
            vesselList
              .map((v) => {
                const id = Number((v as any).companyAdmin?.id ?? (v as any).companyId)
                const cid = Number((v as any).companyGroupAdmin?.id ?? (v as any).companyGroupId)
                const name =
                  (v as any).companyAdmin?.companyName ??
                  (v as any).companyAdmin?.name ??
                  `Subcompany ${id}`
                return id && cid ? ([id, {id, name, companyId: cid}] as const) : null
              })
              .filter(Boolean) as Array<[number, {id: number; name: string; companyId: number}]>
          ).values()
        )

        setCompanies(companiesLite)
        setSubcompanies(subcompaniesLite)
      } catch (e) {
        console.error(e)
        setVessels([])
        setKinds([])
      }
    }
    load()
  }, [])

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      vessel: globalState.vessel?.name || '',
    }))
    setCurrentPage(1)
  }, [globalState.vessel, globalState.company])

  useEffect(() => {
    ;(async () => {
      const [k] = await Promise.all([listAuditKinds()])
      setKinds(Array.isArray(k) ? k : [])

      const vesselList: Vessel[] = normalizeAccessibleVessels(
        await fetchAccessibleVesselsForCurrentUser()
      )

      const [groups, admins] = await Promise.all([
        getCompanyAdminList?.().catch(() => []),
        getCompanyList?.().catch(() => []),
      ])

      setCompanies(
        Array.isArray(groups)
          ? groups.map((g: any) => ({
              id: Number(g.id),
              name: g.name ?? g.companyGroupAdminName ?? `-`,
            }))
          : []
      )

      const derivedFromVessels = (vesselList || [])
        .map((v: any) => ({
          id: Number(v?.companyAdmin?.id ?? v?.companyId),
          name:
            v?.companyAdmin?.name ??
            v?.companyAdminName ??
            v?.companyName ??
            (v?.companyAdmin?.id ? `-` : null),
          companyId: Number(v?.companyGroupAdmin?.id ?? v?.companyGroupId ?? v?.cgaid?.id),
        }))
        .filter((sc) => Number.isFinite(sc.id) && !!sc.name && Number.isFinite(sc.companyId))

      const fromAdmins = Array.isArray(admins)
        ? admins.map((a: any) => ({
            id: Number(a.id),
            name: a.name ?? a.companyAdminName ?? `-`,
            companyId: Number(
              a.cgaid?.id ?? a.cga?.id ?? a.companyGroupAdminId ?? a.companyGroupId
            ),
          }))
        : []

      const subMerged = new Map<number, {id: number; name: string; companyId: number}>()
      ;[...fromAdmins, ...derivedFromVessels].forEach((sc) => {
        if (!Number.isFinite(sc.id) || !Number.isFinite(sc.companyId)) return
        if (!subMerged.has(sc.id)) subMerged.set(sc.id, sc)
      })
      setSubcompanies(Array.from(subMerged.values()))

      setVessels(
        (vesselList || [])
          .filter((v: any) => v?.active)
          .map((v: any) => ({
            id: Number(v.id),
            name: v.fleet_name || v.name || `Vessel ${v.id}`,
            companyGroupAdminId: Number(v?.companyGroupAdmin?.id ?? v?.companyGroupId),
            companyAdminId: Number(v?.companyAdmin?.id ?? v?.companyId),
          }))
      )
    })().catch(console.error)
  }, [])

  const allowedCompanyVesselIds = useMemo(() => {
    if (!globalState.company) return null
    return new Set((globalVessels || []).map((gv: any) => Number(gv.id)))
  }, [globalState.company, globalVessels])

  const allowedCompanyVesselNames = useMemo(() => {
    if (!globalState.company) return null
    return new Set(
      (globalVessels || [])
        .map((gv: any) => (gv.fleet_name || gv.name || '').trim().toLowerCase())
        .filter(Boolean)
    )
  }, [globalState.company, globalVessels])

  const allAvailableVessels = useMemo<VesselLite[]>(() => {
    const map = new Map<string, VesselLite>()

    // 1. From local vessels state
    ;(vessels || []).forEach((v) => {
      const name = (v?.name || '').trim()
      if (name) map.set(name.toLowerCase(), v)
    })

    // 2. From globalVessels in GlobalFilterContext
    ;(globalVessels || []).forEach((gv: any) => {
      const name = (gv.fleet_name || gv.name || `Vessel ${gv.id}`).trim()
      if (name && !map.has(name.toLowerCase())) {
        map.set(name.toLowerCase(), {
          id: Number(gv.id),
          name,
          companyGroupAdminId: Number(gv?.companyGroupAdmin?.id ?? gv?.companyGroupId ?? gv?.companyGroupAdminId),
          companyAdminId: Number(gv?.companyAdmin?.id ?? gv?.companyId ?? gv?.companyAdminId),
          vesselType: gv.vesselType || gv.type,
        })
      }
    })

    // 3. From loaded plans - if plans exist in database for a vessel, that vessel must appear in planning
    ;(plans || []).forEach((p: any) => {
      const rawName = p.vesselName || vesselMap[p.vesselId]
      const name = rawName ? String(rawName).trim() : ''
      if (name && !map.has(name.toLowerCase())) {
        map.set(name.toLowerCase(), {
          id: Number(p.vesselId),
          name,
        })
      }
    })

    return Array.from(map.values())
  }, [vessels, globalVessels, plans, vesselMap])

  const vesselsForFilters = useMemo<VesselLite[]>(() => {
    let list = allAvailableVessels
    if (globalState.company) {
      list = list.filter((v) => {
        const vId = Number(v.id)
        const vName = (v.name || '').trim().toLowerCase()
        if (allowedCompanyVesselIds && allowedCompanyVesselIds.has(vId)) return true
        if (allowedCompanyVesselNames && allowedCompanyVesselNames.has(vName)) return true
        if (globalState.company?.type === 'company_group' && (v as any).companyGroupAdminId) {
          return Number((v as any).companyGroupAdminId) === Number(globalState.company.id)
        }
        if (globalState.company?.type === 'company_admin' && (v as any).companyAdminId) {
          return Number((v as any).companyAdminId) === Number(globalState.company.id)
        }
        return false
      })
    }
    if (planFilters.companyId) {
      const cid = Number(planFilters.companyId)
      list = list.filter(
        (v) => Number((v as any).companyGroupAdminId ?? (v as any).companyGroupId) === cid
      )
    }
    if (planFilters.subcompanyId) {
      const scid = Number(planFilters.subcompanyId)
      list = list.filter((v) => Number((v as any).companyAdminId ?? (v as any).companyId) === scid)
    }
    return list
  }, [
    allAvailableVessels,
    globalState.company,
    allowedCompanyVesselIds,
    allowedCompanyVesselNames,
    planFilters.companyId,
    planFilters.subcompanyId,
  ])

  const effectiveCompanyGroupId = useMemo(() => {
    if (globalState.company?.type === 'company_group') {
      return Number(globalState.company.id)
    }
    return effectiveCompanyId
  }, [globalState.company, effectiveCompanyId])

  const loadPlans = async () => {
    try {
      setLoading(true)
      const list = await listAuditPlans({year: filters.year, companyGroupId: effectiveCompanyGroupId})
      setPlans(Array.isArray(list) ? list : [])
    } catch (e) {
      console.error(e)
      toastApiError(e, 'Failed to load audit plans')
      setPlans([])
    } finally {
      setLoading(false)
    }
  }

  const openViewAuditFromPlan = async (auditId: number) => {
    if (!canReadAudit) {
      toast.error('You do not have access to view this audit.', {position: 'top-center'})
      return
    }
    try {
      const dto = await getAudit(auditId)

      const patched: AuditDto = {
        ...dto,
        vesselName: dto.vesselName || vesselMap[dto.vesselId] || `-`,
        vesselType: dto.vesselType || vesselTypeMap[dto.vesselId] || undefined,
        auditKindName:
          dto.auditKindName || (dto.auditKindId != null ? kindMap[dto.auditKindId] || `-` : ''),
        fromPortName:
          dto.fromPortName || (dto.fromPortId != null ? portMap[dto.fromPortId] || '' : ''),
        toPortName: dto.toPortName || (dto.toPortId != null ? portMap[dto.toPortId] || '' : ''),
        internalAuditorName: dto.internalAuditorName || undefined,
      }

      setViewing(patched)
      setViewOpen(true)
    } catch (e) {
      console.error('Failed to load audit for viewing', e)
      toastApiError(e, 'Failed to load audit')
    }
  }

  useEffect(() => {
    loadPlans()
  }, [filters.year, effectiveCompanyGroupId])

  // Add filter change handler
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const {name, value} = e.target
    setFilters((prev) => ({
      ...prev,
      [name]: name === 'year' ? parseInt(value) : value,
    }))
    setCurrentPage(1) // Reset to first page when filters change
  }

  const handleSort = (key: string) => {
    let direction = 'asc'
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({key, direction})
  }

  // Apply filters to vessels
  const vesselNames: string[] = useMemo(() => allAvailableVessels.map((v) => v.name), [allAvailableVessels])

  // Use a distinct name to avoid any shadowing/collision with functions/props
  const filteredVesselNames = useMemo<string[]>(() => {
    let list = allAvailableVessels
    if (globalState.company) {
      list = list.filter((v) => {
        const vId = Number(v.id)
        const vName = (v.name || '').trim().toLowerCase()
        if (allowedCompanyVesselIds && allowedCompanyVesselIds.has(vId)) return true
        if (allowedCompanyVesselNames && allowedCompanyVesselNames.has(vName)) return true
        if (globalState.company?.type === 'company_group' && (v as any).companyGroupAdminId) {
          return Number((v as any).companyGroupAdminId) === Number(globalState.company.id)
        }
        if (globalState.company?.type === 'company_admin' && (v as any).companyAdminId) {
          return Number((v as any).companyAdminId) === Number(globalState.company.id)
        }
        return false
      })
    }
    let names = list.map((v) => v.name)
    if (filters.vessel) {
      const q = filters.vessel.trim().toLowerCase()
      names = names.filter((v) => v.trim().toLowerCase() === q)
    }
    return names
  }, [
    allAvailableVessels,
    globalState.company,
    allowedCompanyVesselIds,
    allowedCompanyVesselNames,
    filters.vessel,
  ])

  const plansByVesselAndMonth = useMemo(() => {
    const grouped: Record<string, Record<string, AuditPlan[]>> = {}

    // init all visible vessels x months
    for (const v of filteredVesselNames) {
      // build month buckets once
      grouped[v] = Object.fromEntries(ALL_MONTHS.map((m) => [m, [] as AuditPlan[]]))
    }

    // push plans using safe names (via toViewPlan)
    ;(filteredPlans ?? []).map(toViewPlan).forEach((p: AuditPlan) => {
      const vName = p.vesselName
      if (!vName || vName === '-') return
      if (!grouped[vName]) {
        // if a plan’s vessel isn’t currently visible, skip safely
        grouped[vName] = Object.fromEntries(ALL_MONTHS.map((m) => [m, [] as AuditPlan[]]))
      }
      if (!grouped[vName][p.month]) grouped[vName][p.month] = []
      grouped[vName][p.month].push(p)
    })

    return grouped
  }, [filteredVesselNames, filteredPlans, toViewPlan])

  const sortedVessels = useMemo<string[]>(() => {
    const vesselSet = new Set(filteredVesselNames)
    // If no vessel filter is active, also ensure any vessel with plans for this year is visible
    if (!filters.vessel && !globalState.company) {
      Object.keys(plansByVesselAndMonth).forEach((vn) => {
        if (vn && vn !== '-') vesselSet.add(vn)
      })
    }
    const sortableVessels = Array.from(vesselSet)
    sortableVessels.sort((a, b) => {
      if (a < b) return sortConfig.direction === 'asc' ? -1 : 1
      if (a > b) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })
    return sortableVessels
  }, [filteredVesselNames, plansByVesselAndMonth, filters.vessel, globalState.company, sortConfig])

  const totalPages = Math.ceil(sortedVessels.length / rowsPerPage)

  const currentVesselRecords = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    return sortedVessels.slice(startIndex, endIndex)
  }, [sortedVessels, currentPage, rowsPerPage])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setCurrentPage(1)
  }

  const handleAddPlan = async (data: NewPlanData, _saveAndAddNew: boolean) => {
    // convert months ['Jan','Feb'] -> [1,2]
    const monthIndex = (m: typeof ALL_MONTHS[number]) => ALL_MONTHS.indexOf(m) + 1
    const months = data.months.map(monthIndex).filter((n) => n > 0)

    await bulkCreateAuditPlans({
      year: data.year,
      auditKindId: Number(data.auditName),
      vesselIds: data.vesselNames.map((v) => Number(v)),
      months,
    })
    await loadPlans()
  }

  const handleBulkSubmit = async (
    data: {year: number; auditKindId: number; vesselIds: number[]; months: number[]},
    saveAndAddNew: boolean
  ) => {
    await bulkCreateAuditPlans({
      year: data.year,
      auditKindId: data.auditKindId,
      vesselIds: data.vesselIds,
      months: data.months,
    })
    await loadPlans()
    if (!saveAndAddNew) setIsModalVisible(false)
  }

  return (
    <div className='app-main flex-column flex-row-fluid'>
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
          <div className='card'>
            {/* Header */}
            <div className='card-header border-0 pt-6 d-flex justify-content-between bg-white'>
              <div>
                <h3 className='card-label text-dark fw-bold'>Audit Planning</h3>
              </div>
              <div className='card-toolbar'>
                {canCreatePlan ? (
                  <button
                    type='button'
                    className='btn btn_primary'
                    onClick={() => setIsModalVisible(true)}
                  >
                    <SVGIcon
                      path='M11 11V5C11 4.4 11.4 4 12 4C12.6 4 13 4.4 13 5V11H19C19.6 11 20 11.4 20 12C20 12.6 19.6 13 19 13H13V19C13 19.6 12.6 20 12 20C11.4 20 11 19.6 11 19V13H5C4.4 13 4 12.6 4 12C4 11.4 4.4 11 5 11H11Z'
                      className='svg-icon-2'
                    />
                    Add Plan
                  </button>
                ) : null}
              </div>
            </div>

            {/* Filters Section */}
            <div className='card-body py-4 bg-white border-top'>
              <div className='row gx-3 gy-3 mb-4'>

                {/* Year Filter */}
                <div className='col-md-auto'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    Year
                  </label>
                  <SearchableSelect
                    className='form-select'
                    style={{
                      border: '1px solid #E4E6EF',
                      borderRadius: '6px',
                      fontSize: '14px',
                      padding: '8px 12px',
                      paddingRight: '35px',
                      color: '#5E6278',
                      backgroundColor:
                        filters.year === new Date().getFullYear() ? '#f8f9fa' : '#fff',
                    }}
                    name='year'
                    value={filters.year}
                    onChange={handleFilterChange}
                  >
                    {filterData.years.map((year) => (
                      <option
                        key={year.value}
                        value={year.value}
                        style={{
                          backgroundColor: year.isCurrent ? '#e3f2fd' : '#fff',
                          fontWeight: year.isCurrent ? 'bold' : 'normal',
                        }}
                      >
                        {year.label} {year.isCurrent ? '(Current)' : ''}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              </div>

              {/* Status Legend */}
              <StatusLegend />

              {/* Table */}
              <div className='report-table table-responsive'>
                <div style={{overflowX: 'auto'}}>
                  <table className='table table-bordered align-middle'>
                    <thead className='table-header text-center'>
                      <tr style={{height: '38px', backgroundColor: '#f8f9fa'}}>
                        <th
                          className='text-center'
                          style={{
                            minWidth: '50px',
                            width: '50px',
                            position: 'sticky',
                            left: 0,
                            zIndex: 3,
                            backgroundColor: '#f8f9fa',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: '#7e8299',
                          }}
                        >
                          #
                        </th>
                        <th
                          onClick={() => handleSort('vesselName')}
                          className='cursor-pointer'
                          style={{
                            minWidth: '160px',
                            width: '160px',
                            textAlign: 'left',
                            position: 'sticky',
                            left: 50,
                            backgroundColor: '#f8f9fa',
                            zIndex: 2,
                            fontSize: '11px',
                            fontWeight: 600,
                            color: '#7e8299',
                            paddingLeft: '12px',
                          }}
                        >
                          <div className='d-flex align-items-center gap-1'>
                            VESSEL
                            <KTSVG
                              path={`/media/map/sort-col-${
                                sortConfig.key === 'vesselName'
                                  ? sortConfig.direction === 'asc'
                                    ? 'up-black'
                                    : 'down-black'
                                  : 'grey'
                              }.svg`}
                              className='svg-icon svg-icon-6'
                            />
                          </div>
                        </th>
                        {ALL_MONTHS.map((month) => (
                          <th
                            key={month}
                            style={{
                              minWidth: '130px',
                              fontSize: '11px',
                              fontWeight: 600,
                              color: '#7e8299',
                              backgroundColor: '#f8f9fa',
                            }}
                          >
                            {month.toUpperCase()}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className='table-body'>
                      {loading ? (
                        <PlanningCalendarSkeleton rows={rowsPerPage || 8} />
                      ) : currentVesselRecords.length === 0 ? (
                        <tr>
                          <td colSpan={14} className='text-center py-10 text-muted'>
                            No audit plans found for the selected year and vessel.
                          </td>
                        </tr>
                      ) : (
                        currentVesselRecords.map((vessel, index) => (
                        <tr key={vessel || index} style={{borderBottom: '1px solid #eff2f5'}}>
                          {/* SR/NO */}
                          <td
                            className='text-center'
                            style={{
                              position: 'sticky',
                              left: 0,
                              backgroundColor: index % 2 === 0 ? '#fff' : '#fafbff',
                              zIndex: 2,
                              fontSize: '12px',
                              color: '#b5b5c3',
                              verticalAlign: 'middle',
                              padding: '8px 4px',
                              width: '50px',
                            }}
                          >
                            {(currentPage - 1) * rowsPerPage + index + 1}
                          </td>
                          {/* Vessel Name */}
                          <td
                            style={{
                              position: 'sticky',
                              left: 50,
                              backgroundColor: index % 2 === 0 ? '#fff' : '#fafbff',
                              zIndex: 1,
                              fontSize: '12px',
                              fontWeight: 600,
                              color: '#3f4254',
                              verticalAlign: 'middle',
                              padding: '8px 12px',
                              whiteSpace: 'nowrap',
                              width: '160px',
                            }}
                            title={vessel}
                          >
                            {vessel}
                          </td>
                          {/* Month cells */}
                          {ALL_MONTHS.map((month) => {
                            const cellPlans = plansByVesselAndMonth[vessel]?.[month] ?? []
                            return (
                              <td
                                key={`${vessel}-${month}`}
                                style={{
                                  verticalAlign: 'top',
                                  padding: '6px 5px',
                                  backgroundColor: index % 2 === 0 ? '#fff' : '#fafbff',
                                }}
                              >
                                {cellPlans.length > 0 && (
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '3px',
                                      maxHeight: '120px',
                                      overflowY: 'auto',
                                      overflowX: 'hidden',
                                      scrollbarWidth: 'thin',
                                    }}
                                  >
                                    {cellPlans.map((plan) => {
                                      const status = getPlanStatus(plan)
                                      const c = getStatusColorClasses(status)
                                      // find the real plan dto by id
                                      const dto = plans.find((p) => p.id === plan.id)
                                      return (
                                        <button
                                          type='button'
                                          key={plan.id}
                                          title='Click for actions'
                                          onClick={() => {
                                            if (!canReadPlan) {
                                              toast.error('You do not have access to open plan actions.', {position: 'top-center'})
                                              return
                                            }
                                            setSelectedPlan(dto || null)
                                            setPlanActionsOpen(true)
                                          }}
                                          style={{
                                            display: 'block',
                                            width: '100%',
                                            padding: '4px 8px',
                                            backgroundColor: c.backgroundColor,
                                            borderLeft: `3px solid ${c.borderColor}`,
                                            border: `1px solid ${c.borderColor}`,
                                            borderLeftWidth: '3px',
                                            color: c.textColor,
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '11px',
                                            fontWeight: 500,
                                            lineHeight: '1.4',
                                            textAlign: 'left',
                                            wordBreak: 'break-word',
                                          }}
                                        >
                                          {plan.auditName}
                                          {dto?.auditId !== undefined && (
                                            <span
                                              style={{
                                                display: 'inline-block',
                                                marginLeft: '4px',
                                                fontSize: '9px',
                                                fontWeight: 700,
                                                opacity: 0.6,
                                                color: dto.auditId ? '#1bc5bd' : '#a1a5b7',
                                              }}
                                            >
                                              {dto.auditId ? '● LNK' : '○ UNL'}
                                            </span>
                                          )}
                                        </button>
                                      )
                                    })}
                                  </div>
                                )}
                              </td>
                            )
                          })}
                        </tr>
                      ))
                    )}
                    </tbody>
                  </table>

                </div>

                <div className='pagination-wrapper d-flex justify-content-between align-items-center py-3'>
                  <div className='d-flex align-items-center'>
                    <span className='text-muted me-2'>Rows per page</span>
                    <SearchableSelect
                      className='form-select'
                      style={{
                        borderRadius: '20px',
                        width: '70px',
                        border: '1px solid #dee2e6',
                        fontSize: '14px',
                        padding: '4px 8px',
                      }}
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </SearchableSelect>
                  </div>
                  <div className='d-flex align-items-center'>
                    <span className='text-muted me-3' style={{fontSize: '14px'}}>
                      Showing{' '}
                      <strong>
                        {sortedVessels.length > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0}-
                        {Math.min(currentPage * rowsPerPage, sortedVessels.length)}
                      </strong>{' '}
                      of <strong>{sortedVessels.length}</strong>
                    </span>
                    <nav>
                      <ul className='pagination pagination-sm mb-0' style={{gap: '2px'}}>
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className='page-link text-muted'
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              padding: '8px 12px',
                              fontSize: '14px',
                              borderRadius: '6px',
                            }}
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            ‹
                          </button>
                        </li>
                        {Array.from(Array(totalPages).keys()).map((num) => (
                          <li
                            key={num + 1}
                            className={`page-item ${currentPage === num + 1 ? 'active' : ''}`}
                          >
                            <button
                              className='page-link text-muted'
                              style={{
                                backgroundColor:
                                  currentPage === num + 1 ? '#F4F9FF' : 'transparent',
                                border: '1px solid #dee2e6',
                                padding: '8px 12px',
                                fontSize: '14px',
                                minWidth: '40px',
                                borderRadius: '6px',
                              }}
                              onClick={() => handlePageChange(num + 1)}
                            >
                              {num + 1}
                            </button>
                          </li>
                        ))}
                        <li
                          className={`page-item ${
                            currentPage === totalPages || totalPages === 0 ? 'disabled' : ''
                          }`}
                        >
                          <button
                            className='page-link text-muted'
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              padding: '8px 12px',
                              fontSize: '14px',
                              borderRadius: '6px',
                            }}
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || totalPages === 0}
                          >
                            ›
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AddAuditPlanModal
        visible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        onSubmit={handleBulkSubmit}
        kinds={kinds}
        vessels={vesselsForFilters}
      />

      {/* Plan Actions */}
      <AuditPlanActionsModal
        visible={planActionsOpen}
        onClose={() => {
          setPlanActionsOpen(false)
          setSelectedPlan(null)
        }}
        plan={selectedPlan}
        refreshPlans={loadPlans}
        canCreateAndLink={canCreateAudit && canUpdatePlan}
        canViewAudit={canReadAudit}
        canLinkExisting={canUpdatePlan}
        onViewAudit={(auditId) => {
          setPlanActionsOpen(false)
          openViewAuditFromPlan(Number(auditId))
        }}
        onCreateAndLink={(prefill) => {
          setPlanActionsOpen(false)
          setAddFromPlan(prefill) // this alone shows AddAuditModal (visible={Boolean(addFromPlan)})
        }}
      />

      <ViewAuditModal
        visible={viewOpen}
        onClose={() => {
          setViewOpen(false)
          setViewing(null)
        }}
        record={viewing}
      />

      {addFromPlan && (
        <AddAuditModal
          visible={Boolean(addFromPlan)}
          onClose={() => setAddFromPlan(null)}
          onSubmit={async (data) => {
            const payload = {
              vesselId: Number(data.vessel),
              auditKindId: Number(data.audit),
              auditType: (data.auditType as any) || 'DETAILS_TYPE',
              auditFromDate: data.auditDate,
              auditToDate: data.auditDate,
              internalAuditorName: data.internalAuditorName || undefined,
              externalAuditorName: data.externalAuditorName || undefined,
              fromPortId: data.fromPort ? Number(data.fromPort) : undefined,
              toPortId: data.toPort ? Number(data.toPort) : undefined,
              hoursOnboard: data.hoursOnboard ? Number(data.hoursOnboard) : undefined,
              remarks: data.auditRemarks || undefined,
            } as any

            try {
              const created = await createAudit(payload)
              if (!created?.id) throw new Error('Create audit failed')

              await linkAuditPlanToAudit(Number(addFromPlan.planId), Number(created.id))

              await loadPlans()
            } catch (err) {
              console.error('Create+Link audit failed', err)
              toastApiError(err, 'Create or link audit failed')
            } finally {
              setAddFromPlan(null)
            }
          }}
          vessels={vessels}
          kinds={kinds}
          ports={portsList}
          planOptions={[]}
          companies={companies}
          subcompanies={subcompanies}
          crews={crews}
          rankMap={rankMap}
          showCompanyFilters={!shipboardUser}
          isCrew={shipboardUser}
          prefill={{
            vesselId: addFromPlan.vesselId,
            auditKindId: addFromPlan.auditKindId,
            planId: addFromPlan.planId,
            planMonthDate: addFromPlan.planMonthDate,
          }}
          lockPlanContext
        />
      )}
    </div>
  )
}

export default AuditPlanning
